import { z } from 'zod';
import { fieldSpecSchema, type FieldSpec } from './field.js';

/** Model v2 — React components with typed fields; sections carry values. */

export const bindValueSchema = z.object({ $bind: z.string().min(1) });
export type BindValue = z.infer<typeof bindValueSchema>;

/** A stored field value: a literal (string/number/array/object) or a binding. */
export const valueSchema: z.ZodType<unknown> = z.unknown();
export function isBind(v: unknown): v is BindValue {
  return typeof v === 'object' && v !== null && '$bind' in v &&
    typeof (v as Record<string, unknown>)['$bind'] === 'string';
}

export const componentOriginSchema = z.enum(['builtin', 'custom']);
export type ComponentOrigin = z.infer<typeof componentOriginSchema>;

export const componentDefV2Schema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string().optional(),
  origin: componentOriginSchema,
  /** custom components only: the TSX source (compiled client-side) */
  source: z.string().optional(),
  fields: z.array(fieldSpecSchema),
});
export type ComponentDefV2 = z.infer<typeof componentDefV2Schema> & { fields: FieldSpec[] };

export const sectionRepeatSchema = z.object({
  over: z.string().min(1),
  as: z.string().min(1),
});
export type SectionRepeat = z.infer<typeof sectionRepeatSchema>;

export const sectionInstanceSchema = z.object({
  id: z.string().min(1),
  componentId: z.string().min(1),
  values: z.record(z.string(), valueSchema).default({}),
  repeat: sectionRepeatSchema.optional(),
});
export type SectionInstance = z.infer<typeof sectionInstanceSchema>;

export const documentV2Schema = z.object({
  version: z.literal(2).default(2),
  id: z.string().min(1),
  name: z.string().min(1),
  themeCss: z.string(),
  data: z.record(z.string(), z.unknown()),
  sections: z.array(sectionInstanceSchema),
  updatedAt: z.string().optional(),
});
export type DocumentV2 = z.infer<typeof documentV2Schema>;

/** What the API stores/serves: the document plus any custom components it uses. */
export const documentBundleV2Schema = z.object({
  document: documentV2Schema,
  customComponents: z.array(componentDefV2Schema).default([]),
});
export type DocumentBundleV2 = z.infer<typeof documentBundleV2Schema>;
